import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  useStripe,
  useElements,
  Elements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import FrontBanner from "@/assets/images/front-banner.svg";
import { ROUTESLIST } from "@/static/urls";
import { membershipPackages } from "@/utils/constants";
import { getCheckoutStripePublishKey } from "@/utils/stripeCheckoutConfig";
import { MEMBERSHIP_API_URL } from "@/static/api";
import "../checkout1/index.scss";
import "./InPlace.scss";

const TIER_TO_PACKAGE_ID = { pro: "ample-samples-pro", lite: "ample-samples-lite" };

// Membership checkout uses its own config (sandbox/live + mode). See REACT_APP_CHECKOUT_STRIPE_* in .env.
const checkoutStripePublishKey = getCheckoutStripePublishKey();
const stripePromise =
  checkoutStripePublishKey
    ? loadStripe(checkoutStripePublishKey)
    : null;

const appearance = {
  theme: "flat",
  variables: {
    colorPrimary: "#ffffff",
    colorText: "#ffffff",
    colorBackground: "#1f1f1f",
    colorDanger: "#ff4d4f",
    colorTextPlaceholder: "#bbbbbb",
    spacingUnit: "6px",
    borderRadius: "6px",
  },
  rules: {
    ".Input, .Block": {
      backgroundColor: "#2a2a2a",
      border: "1px solid #444",
      color: "#ffffff",
    },
    ".Label": {
      color: "#ffffff",
      fontWeight: "500",
    },
    ".Error": {
      color: "#ff6b6b",
    },
  },
};

const inputStyle = {
  padding: 10,
  backgroundColor: "#2a2a2a",
  borderRadius: 10,
  color: "#fff",
  border: "1px solid #444",
  width: "100%",
  boxSizing: "border-box",
};

function PaymentForm({
  email,
  phone,
  businessName,
  tier,
  pkg,
  onSuccess,
  onError,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [paymentElementReady, setPaymentElementReady] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !paymentElementReady) return;
    if (!email.trim()) {
      onError("Email is required.");
      return;
    }
    if (!phone.trim()) {
      onError("Phone number is required.");
      return;
    }

    setIsLoading(true);
    onError(null);

    try {
      const { setupIntent, error } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });

      if (error) {
        onError(error.message || "Payment failed.");
        setIsLoading(false);
        return;
      }

      if (setupIntent.status !== "succeeded") {
        onError("Payment was not completed.");
        setIsLoading(false);
        return;
      }

      const res = await fetch(`${MEMBERSHIP_API_URL}/create-membership-from-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paymentMethodId: setupIntent.payment_method,
          email: email.trim(),
          phone: phone.trim(),
          businessName: businessName ? businessName.trim() : null,
          tier,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        onError(data?.message || data?.error || "Could not complete membership.");
        setIsLoading(false);
        return;
      }

      if (data?.success === false) {
        onError(data?.message || "Could not complete membership.");
        setIsLoading(false);
        return;
      }

      onSuccess();
    } catch (err) {
      onError(err?.message || "Something went wrong.");
      setIsLoading(false);
    }
  };

  return (
    <div className="membership-inplace-form">
      <form onSubmit={handleSubmit}>
        {isLoading && (
          <div className="loading-overlay">
            <div className="loader" />
          </div>
        )}
        <div className="membership-inplace-payment-heading">Payment details</div>
        <div className="membership-inplace-payment-element">
          <PaymentElement
            onReady={() => setPaymentElementReady(true)}
            options={{
              defaultValues: {
                billingDetails: {
                  email: email.trim() || undefined,
                  name: businessName.trim() || undefined,
                  phone: phone.trim() || undefined,
                },
              },
            }}
          />
        </div>
        <button
          type="submit"
          className="btn btn-primary membership-inplace-pay-btn"
          disabled={!stripe || !paymentElementReady || isLoading}
        >
          {isLoading ? "Processing…" : "Pay now"}
        </button>
      </form>
    </div>
  );
}

function MembershipCheckoutInPlace() {
  const location = useLocation();
  const history = useHistory();
  const [pkg, setPkg] = useState(null);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [clientSecret, setClientSecret] = useState(null);
  const [loadingSecret, setLoadingSecret] = useState(true);
  const [error, setError] = useState(null);

  const tier = new URLSearchParams(location.search).get("tier")?.toLowerCase();
  const packageId = tier ? TIER_TO_PACKAGE_ID[tier] : null;

  useEffect(() => {
    if (!packageId) {
      history.replace(ROUTESLIST.PACKAGES);
      return;
    }
    const found = membershipPackages.find((p) => p.id === packageId);
    setPkg(found || null);
    if (!found) history.replace(ROUTESLIST.PACKAGES);
  }, [packageId, history]);

  useEffect(() => {
    if (!tier || !pkg) return;

    let cancelled = false;
    setLoadingSecret(true);
    setError(null);

    fetch(`${MEMBERSHIP_API_URL}/create-membership-setup-intent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tier, email: email || undefined }),
    })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        if (cancelled) return;
        if (data?.clientSecret) setClientSecret(data.clientSecret);
        else setError(data?.message || data?.error || "Could not load payment form.");
      })
      .catch((err) => {
        if (!cancelled)
          setError(err?.message || "Cannot reach checkout API. Check CORS and URL.");
      })
      .finally(() => {
        if (!cancelled) setLoadingSecret(false);
      });

    return () => { cancelled = true; };
  }, [tier, pkg]);

  const handleSuccess = () => {
    history.push(ROUTESLIST.CHECKOUT_MEMBERSHIP_SUCCESS, {
      fromInPlace: true,
      message: "Payment successful. We've created your account — check the email you used to sign in.",
    });
  };

  if (!pkg) return null;

  return (
    <main className="page page-shop membership-checkout-inplace">
      <div className="product-section">
        <div
          className="product-header"
          style={{ justifyContent: "space-between" }}
        >
          <div
            className="back-to-home-section"
            onClick={() => history.push(ROUTESLIST.PACKAGES)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              e.key === "Enter" && history.push(ROUTESLIST.PACKAGES)
            }
          >
            <ArrowBackIcon />
          </div>
          <div className="back-to-home-section-image">
            <img src={FrontBanner} alt="Ample Samples" />
          </div>
          <div>&nbsp;</div>
        </div>

        <div className="subscription-section membership-inplace-wrap">
          <header className="membership-inplace-header">
            <h2 className="membership-inplace-title">Membership checkout</h2>
            <p className="membership-inplace-subtitle">
              No account needed. Enter your details and pay below.
            </p>
          </header>

          <div className="subscription-section-center membership-inplace-card-wrap">
            <div className="membership-inplace-card">
              <section className="membership-inplace-summary">
                <div className="plan-name">
                  <h3>{pkg.name}</h3>
                  {pkg.badge && (
                    <span className="membership-tile__badge">⭐ {pkg.badge}</span>
                  )}
                </div>
                <div className="plan-price">
                  <h3>${pkg.price}</h3>
                  <span>USD</span>
                  <p>{pkg.description}</p>
                </div>
                <div className="subscription-subtotal">
                  <div className="summary-details">
                    <h3>1× {pkg.name}</h3>
                    <p>${pkg.price}</p>
                  </div>
                  <div className="divider" />
                  <div className="payable-amout">
                    <p>Total</p>
                    <h3>${pkg.price} USD</h3>
                  </div>
                </div>
              </section>

              <section className="membership-inplace-fields">
                <h4 className="membership-inplace-section-title">Your details</h4>
                <label>Email *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  style={inputStyle}
                  required
                />
                <label>Phone *</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 234 567 8900"
                  style={inputStyle}
                  required
                />
                <label>Business name (optional)</label>
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="Your company or label"
                  style={inputStyle}
                />
              </section>

              {error && (
                <p className="membership-checkout-error">{error}</p>
              )}

              {loadingSecret && (
                <div className="loading-overlay">
                  <div className="loader" />
                </div>
              )}

              {!stripePromise && !loadingSecret && (
                <p className="membership-inplace-error">
                  Payment is not configured. Edit checkoutStripeMode and keys in src/utils/stripeCheckoutConfig.js to match your backend.
                </p>
              )}

              {!loadingSecret && clientSecret && stripePromise && (
                <section className="membership-inplace-payment-section">
                  <Elements
                    stripe={stripePromise}
                    options={{ clientSecret, appearance }}
                  >
                    <PaymentForm
                      email={email}
                      phone={phone}
                      businessName={businessName}
                      tier={tier}
                      pkg={pkg}
                      onSuccess={handleSuccess}
                      onError={setError}
                    />
                  </Elements>
                </section>
              )}

              {!loadingSecret && !clientSecret && !error && stripePromise && (
                <p className="membership-inplace-error">
                  Could not load payment form. Please try again.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default MembershipCheckoutInPlace;
